// frontend/packages/business-console/src/pages/BuildWelcome.jsx
//
// Post-payment landing for the build funnel. Stripe Checkout (embedded or
// hosted) returns the customer here (return_url / success_url =
// /build/welcome?bsid=...&session_id=...). It is a PUBLIC page: the customer
// has paid but does not have a Console account yet. Its only job is to confirm
// the payment and tell them the clear next step (check email, create account),
// so they are never dumped on a bare sign-in screen with no guidance.

import React from 'react';
import { Box, Container, Paper, Typography } from '@mui/material';
import { api } from '@upryzze/services';

const SUPPORT_EMAIL = 'support@upryzze.com';

// Resolve the backend API base the same way inboxStream does (axios default
// first, then runtime/build env), then confirm-and-fulfill the payment
// straight from Stripe. This is the belt to the webhook's suspenders (P4): if
// the webhook is missed or misconfigured, this client-side call still stands
// up the account, Business record, and repo. It is idempotent server-side, so
// racing the webhook is harmless.
function apiBase() {
  if (api?.defaults?.baseURL) return String(api.defaults.baseURL);
  if (
    typeof window !== 'undefined' &&
    window.__UPRYZZE_ENV__ &&
    window.__UPRYZZE_ENV__.REACT_APP_API_URL
  ) {
    return String(window.__UPRYZZE_ENV__.REACT_APP_API_URL);
  }
  // eslint-disable-next-line no-undef
  return (
    (typeof process !== 'undefined' &&
      process.env &&
      process.env.REACT_APP_API_URL) ||
    ''
  );
}

function reconcileUrl() {
  const base = apiBase().replace(/\/+$/, '');
  if (!base) return '/api/public/build-signup/reconcile';
  if (/\/api$/i.test(base)) return `${base}/public/build-signup/reconcile`;
  return `${base}/api/public/build-signup/reconcile`;
}

async function reconcileBuild(bsid, sessionId) {
  const res = await fetch(reconcileUrl(), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ bsid, sessionId }),
  });
  if (!res.ok) {
    const err = new Error(`reconcile HTTP ${res.status}`);
    err.status = res.status;
    throw err;
  }
  return res.json();
}

// UpRyzze brand gradient (kept in sync with theme.js brandGradient).
const BRAND_GRADIENT =
  'linear-gradient(120deg, #2563eb 0%, #7c3aed 55%, #ab2ddd 100%)';

const Wordmark = () => (
  <Typography
    component="div"
    sx={{
      fontWeight: 900,
      fontSize: 30,
      letterSpacing: '-0.02em',
      lineHeight: 1,
      mb: 2,
      backgroundImage: BRAND_GRADIENT,
      backgroundClip: 'text',
      WebkitBackgroundClip: 'text',
      color: 'transparent',
      WebkitTextFillColor: 'transparent',
    }}
  >
    UpRyzze
  </Typography>
);

export default function BuildWelcome() {
  // Fire the webhook-independent fulfillment as soon as the customer lands
  // here. Both checkout modes carry ?bsid=...&session_id=... now. Retry a
  // couple of times on transient network failure; a client-side/4xx result
  // (bad params, mismatch) is terminal and not retried. The visible copy does
  // not depend on this (the account email path is unchanged); this only makes
  // provisioning resilient to a dropped webhook.
  React.useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const params = new URLSearchParams(window.location.search);
    const bsid = (params.get('bsid') || '').trim();
    const sessionId = (params.get('session_id') || '').trim();
    if (!bsid || !sessionId) return undefined;

    let cancelled = false;
    const MAX_ATTEMPTS = 3;

    (async () => {
      for (let attempt = 1; attempt <= MAX_ATTEMPTS; attempt += 1) {
        if (cancelled) return;
        try {
          await reconcileBuild(bsid, sessionId);
          return; // success (idempotent): done
        } catch (err) {
          // 4xx is a terminal client error (bad/mismatched session): stop.
          if (err && err.status && err.status >= 400 && err.status < 500) {
            return;
          }
          if (attempt === MAX_ATTEMPTS) return;
          // Linear backoff: 1.5s, 3s.
          await new Promise((r) => setTimeout(r, attempt * 1500));
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background:
          'linear-gradient(135deg, #0f172a 0%, #111827 45%, #1f2937 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        p: 2,
      }}
    >
      <Container maxWidth="sm" disableGutters>
        <Paper elevation={10} sx={{ p: 4, borderRadius: 2 }}>
          <Box sx={{ textAlign: 'center', mb: 2 }}>
            <Wordmark />
            <Typography variant="h5" sx={{ fontWeight: 800 }}>
              Payment received. You are all set.
            </Typography>
          </Box>

          <Typography variant="body1" sx={{ mt: 2, lineHeight: 1.7 }}>
            Thank you. Your build is underway and your spot is locked in.
          </Typography>

          <Typography variant="body1" sx={{ mt: 2, lineHeight: 1.7 }}>
            Next step: check your email. We just sent you a secure link to{' '}
            <strong>create your account</strong> and open your Console. Click
            that link, confirm your email with the code we send, and you are in.
          </Typography>

          <Box
            sx={{
              mt: 3,
              p: 2,
              borderRadius: 2,
              bgcolor: 'action.hover',
            }}
          >
            <Typography variant="body2" sx={{ lineHeight: 1.6 }}>
              Do not see the email within a few minutes? Check your spam folder,
              or email us at{' '}
              <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a> and we
              will resend your link right away.
            </Typography>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
}
